/**
 * Location filtering and sorting for the Trace Studio panel's project and
 * scene grids. Self-contained (no imports from catalog-shared) so it can be
 * consumed by both catalog-shared.tsx and catalog-panel-impl.tsx without
 * creating an import cycle.
 */

import React from 'react';

/** Minimal accessor shape shared by Scene/ProjectAccessorResponseDto. */
export interface AccessorLike {
    accessType?: string | null;
    latitude?: number | null;
    longitude?: number | null;
}

/** Minimal sortable shape shared by SceneResponseDto and ProjectResponseDto. */
export interface CatalogEntryLike {
    name?: string | null;
    dateCreated?: string;
    dateModified?: string | null;
}

export type LocationFilter = 'all' | 'location' | 'anywhere';
export type SortKey = 'default' | 'name' | 'created' | 'updated';

/** True when the entity is location-based (LBX): any accessor pinned to a place. */
export function isLocationBased(accessors: AccessorLike[] | null | undefined): boolean {
    return !!accessors?.some(a => a.accessType === 'Location' || (a.latitude != null && a.longitude != null));
}

export function applyLocationFilter<T>(
    items: T[],
    filter: LocationFilter,
    getAccessors: (item: T) => AccessorLike[] | null | undefined,
): T[] {
    if (filter === 'all') return items;
    return items.filter(item => isLocationBased(getAccessors(item)) === (filter === 'location'));
}

function timestamp(dateStr: string | null | undefined): number {
    const t = dateStr ? Date.parse(dateStr) : NaN;
    return Number.isNaN(t) ? 0 : t;
}

/** Field accessors for {@link sortBy}, for item shapes with non-standard field names. */
export interface SortFieldGetters<T> {
    name: (item: T) => string | null | undefined;
    created: (item: T) => string | null | undefined;
    modified: (item: T) => string | null | undefined;
}

/** Sort a copy of `items`: name ascending, created/updated newest-first, default = server order. */
export function sortBy<T>(items: T[], sort: SortKey, get: SortFieldGetters<T>): T[] {
    const sorted = [...items];
    switch (sort) {
        case 'default':
            break; // keep the order the server sent
        case 'name':
            sorted.sort((a, b) => (get.name(a) ?? '').localeCompare(get.name(b) ?? '', undefined, { sensitivity: 'base' }));
            break;
        case 'created':
            sorted.sort((a, b) => timestamp(get.created(b)) - timestamp(get.created(a)));
            break;
        case 'updated':
            sorted.sort((a, b) =>
                timestamp(get.modified(b) ?? get.created(b)) - timestamp(get.modified(a) ?? get.created(a)));
            break;
    }
    return sorted;
}

/** {@link sortBy} for the standard name/dateCreated/dateModified DTO shape. */
export function sortEntries<T extends CatalogEntryLike>(items: T[], sort: SortKey): T[] {
    return sortBy(items, sort, {
        name: i => i.name,
        created: i => i.dateCreated,
        modified: i => i.dateModified,
    });
}

// ============================================================================
// UI
// ============================================================================

const FILTER_OPTIONS: { value: LocationFilter; label: string; title: string }[] = [
    { value: 'all', label: 'All', title: 'Show all' },
    { value: 'location', label: 'Location', title: 'Only location-based experiences' },
    { value: 'anywhere', label: 'Anywhere', title: 'Only view-anywhere experiences' },
];

const SORT_LABELS: Record<SortKey, string> = {
    default: 'Default',
    updated: 'Updated',
    created: 'Created',
    name: 'Name',
};

const DEFAULT_SORT_KEYS: SortKey[] = ['updated', 'created', 'name'];

const filterButtonStyle = (active: boolean): React.CSSProperties => ({
    padding: '3px 8px',
    fontSize: '11px',
    fontWeight: active ? 600 : 400,
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    backgroundColor: active ? 'var(--colorBrandBackground)' : 'var(--colorNeutralBackground3)',
    color: active ? 'var(--colorNeutralForegroundOnBrand)' : 'var(--colorNeutralForeground2)',
    transition: 'background-color 0.15s, color 0.15s',
});

/**
 * Compact row of location-filter buttons and a sort dropdown for entity
 * grids. Omit `filter`/`onFilterChange` to render a sort-only bar (used by
 * grids whose items have no location, e.g. My Content).
 */
export const FilterSortBar: React.FC<{
    filter?: LocationFilter;
    onFilterChange?: (filter: LocationFilter) => void;
    sort: SortKey;
    onSortChange: (sort: SortKey) => void;
    /** Which sort options to offer, in order (default: updated/created/name). */
    sortKeys?: SortKey[];
}> = ({ filter, onFilterChange, sort, onSortChange, sortKeys }) => (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '6px', flexShrink: 0 }}>
        {filter !== undefined && onFilterChange && FILTER_OPTIONS.map(opt => (
            <button
                key={opt.value}
                style={filterButtonStyle(filter === opt.value)}
                title={opt.title}
                onClick={() => onFilterChange(opt.value)}
            >
                {opt.label}
            </button>
        ))}
        <div style={{ flex: 1 }} />
        <select
            value={sort}
            onChange={e => onSortChange(e.target.value as SortKey)}
            title="Sort by"
            style={{
                fontSize: '11px', padding: '3px 6px', borderRadius: '3px',
                border: '1px solid var(--colorNeutralStroke1)',
                backgroundColor: 'var(--colorNeutralBackground3)',
                color: 'var(--colorNeutralForeground1)',
                cursor: 'pointer', outline: 'none',
            }}
        >
            {(sortKeys ?? DEFAULT_SORT_KEYS).map(key => (
                <option key={key} value={key}>{SORT_LABELS[key]}</option>
            ))}
        </select>
    </div>
);
